import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Space, Table, Tag, Tooltip, message, Modal, Form, Input, Select, Typography } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import ModuleHeader from "../../components/ModuleHeader";
import StatusBadge from "../../components/common/StatusBadge";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import PageLoader from "../../components/common/PageLoader";
import usePermission from "../../hooks/usePermission";
import {
  listSettings,
  createSetting,
  updateSetting,
  deleteSetting,
} from "../../services/settings.service";

const { Text } = Typography;

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const sameId = (a, b) => String(a || "") === String(b || "");

export default function CityZonePage() {
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermission("settings");

  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("city"); // "city" or "zone"
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listSettings("city-zones", { per_page: 100 });
      const allRecords = result.records || [];
      setCities(allRecords.filter((r) => r.entityType === "city"));
      setZones(allRecords.filter((r) => r.entityType === "zone"));
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getZonesForCity = useCallback(
    (cityId) => zones.filter((z) => sameId(z.parentCity, cityId)),
    [zones]
  );

  const unassignedZones = useMemo(
    () => zones.filter((z) => !z.parentCity),
    [zones]
  );

  const handleAdd = (type) => {
    setModalType(type);
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ status: "active", country: "Oman" });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setModalType(record.entityType);
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      parentCity: record.parentCity || undefined,
    });
    setModalOpen(true);
  };

  const handleDelete = (record) => {
    const label = record.entityType === "city" ? "City" : "Zone";
    ConfirmDeleteModal({
      title: `Delete ${label}`,
      content:
        record.entityType === "city"
          ? `Are you sure you want to delete "${record.name}"? Zones under this city will become unassigned and can be mapped to another city later.`
          : `Are you sure you want to delete "${record.name}"?`,
      onConfirm: async () => {
        try {
          const result = await deleteSetting("city-zones", record._id);
          message.success(result?.msg || "Deleted successfully");
          fetchData();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Delete failed");
        }
      },
    });
  };

  const handleFormSubmit = async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        entityType: modalType,
      };

      if (editingRecord) {
        await updateSetting("city-zones", editingRecord._id, data);
        message.success("Updated successfully");
      } else {
        await createSetting("city-zones", data);
        message.success("Created successfully");
      }
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      if (err?.errors) {
        message.error(err.errors[0]?.msg || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const actionRender = (_, record) => (
    <Space size="small">
      {canEdit && (
        <Tooltip title="Edit">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
        </Tooltip>
      )}
      {canDelete && (
        <Tooltip title="Delete">
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
        </Tooltip>
      )}
    </Space>
  );

  const zoneColumns = [
    { title: "Zone Name", dataIndex: "name", key: "name", width: "30%" },
    { title: "Country", key: "country", width: "20%", render: () => "" },
    { title: "Zones", key: "zonePlaceholder", width: "15%", render: () => "" },
    { title: "Status", dataIndex: "status", key: "status", width: "20%", render: (s) => <StatusBadge status={s} /> },
    { title: "Actions", key: "actions", width: "15%", render: actionRender },
  ];

  const unassignedZoneColumns = [
    { title: "Zone Name", dataIndex: "name", key: "name", width: "30%" },
    {
      title: "Parent City",
      key: "parentCity",
      width: "20%",
      render: () => <Tag color="orange">Unassigned</Tag>,
    },
    { title: "Zones", key: "zonePlaceholder", width: "15%", render: () => "" },
    { title: "Status", dataIndex: "status", key: "status", width: "20%", render: (s) => <StatusBadge status={s} /> },
    { title: "Actions", key: "actions", width: "15%", render: actionRender },
  ];

  const cityColumns = [
    { title: "City Name", dataIndex: "name", key: "name", width: "30%" },
    { title: "Country", dataIndex: "country", key: "country", width: "20%" },
    {
      title: "Zones",
      key: "zoneCount",
      width: "15%",
      render: (_, record) => {
        const count = getZonesForCity(record._id).length;
        return <Tag>{count} zone{count !== 1 ? "s" : ""}</Tag>;
      },
    },
    { title: "Status", dataIndex: "status", key: "status", width: "20%", render: (s) => <StatusBadge status={s} /> },
    { title: "Actions", key: "actions", width: "15%", render: actionRender },
  ];

  if (loading) {
    return <PageLoader tip="Loading City & Zone..." />;
  }

  return (
    <div>
      <ModuleHeader
        title="Settings"
        subtitle="City & Zone"
        backTo="/settings"
        onTitleClick={() => navigate("/settings")}
      />

      <div style={{ padding: "0 24px" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 16 }}>
          {canEdit && (
            <Space>
              <Button icon={<PlusOutlined />} onClick={() => handleAdd("city")}>
                Add City
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAdd("zone")}>
                Add Zone
              </Button>
            </Space>
          )}
        </div>

        <Table
          columns={cityColumns}
          dataSource={cities}
          rowKey="_id"
          size="middle"
          pagination={false}
          expandable={{
            expandedRowRender: (city) => {
              const cityZones = getZonesForCity(city._id);
              if (cityZones.length === 0) {
                return <p style={{ margin: 0, color: "#999" }}>No zones defined for this city</p>;
              }
              return (
                <Table
                  columns={zoneColumns}
                  dataSource={cityZones}
                  rowKey="_id"
                  size="small"
                  pagination={false}
                  showHeader={false}
                />
              );
            },
          }}
        />

        {unassignedZones.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 15 }}>Unassigned Zones</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                These zones have no parent city. Edit a zone to map it to a city.
              </Text>
            </div>
            <Table
              columns={unassignedZoneColumns}
              dataSource={unassignedZones}
              rowKey="_id"
              size="middle"
              pagination={false}
            />
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={
          editingRecord
            ? `Edit ${modalType === "city" ? "City" : "Zone"}`
            : `Add ${modalType === "city" ? "City" : "Zone"}`
        }
        open={modalOpen}
        onOk={handleFormSubmit}
        onCancel={() => {
          setModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={modalType === "city" ? "City Name" : "Zone Name"}
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input />
          </Form.Item>

          {modalType === "zone" && (
            <Form.Item
              name="parentCity"
              label="Parent City"
              rules={[{ required: true, message: "Parent city is required" }]}
              extra={
                editingRecord && !editingRecord.parentCity
                  ? "This zone is currently unassigned. Select a city to remap it."
                  : undefined
              }
            >
              <Select
                placeholder="Select city"
                options={cities.map((c) => ({ label: c.name, value: c._id }))}
              />
            </Form.Item>
          )}

          <Form.Item name="country" label="Country">
            <Input />
          </Form.Item>

          <Form.Item name="status" label="Status">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
